/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Brand & logo" on the Templates settings tab: the logo and the two colours
 * used on quotation, order and invoice PDFs, with a live preview of a sample
 * document in the standard design (GET /templates/preview/{type}).
 *
 * The preview shows unsaved colours as you pick them; Save writes them to
 * /company/info as before. The logo uses the same upload as Company
 * Settings (useCompanyLogo).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { ImageUp, RotateCcw, Trash2 } from "lucide-react";
import AttachmentUploadModal from "components/CustomModals/AttachmentUploadModal";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { v2 as t } from "styles/tokens/v2";
import { DEFAULT_ACCENT, DEFAULT_MAIN, isHex, normalizeHex, savedBrandColors, whiteTextReads } from "./brandColors";
import useCompanyLogo from "./useCompanyLogo";

const DOCS = [
  { key: "quote", label: "Quote", module: "QUOTATIONS" },
  { key: "invoice", label: "Invoice", module: "INVOICES" },
  { key: "order", label: "Order", module: "ORDERS" },
];

function ColourField({ label, hint, value, onChange, defaultValue }) {
  const valid = isHex(value);
  const hex = normalizeHex(value, defaultValue);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>
        {label}
      </Typography>
      <Typography component="div" sx={{ fontSize: 13, color: t.text2, mb: 1 }}>
        {hint}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="input"
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={`${label} picker`}
          sx={{ width: 40, height: 36, p: 0, border: `1px solid ${t.border2}`, borderRadius: "8px", background: "transparent", cursor: "pointer" }}
        />
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={!valid}
          inputProps={{ "aria-label": `${label} hex`, maxLength: 7, spellCheck: false }}
          sx={{ width: 120 }}
        />
        <Button size="small" variant="text" startIcon={<RotateCcw size={14} />} onClick={() => onChange(defaultValue)} disabled={hex === defaultValue}>
          PearlCRM default
        </Button>
      </Box>
      {valid && !whiteTextReads(hex) && (
        <Typography sx={{ fontSize: 12.5, color: t.text2, mt: 0.75 }}>
          Light colour: text on it is printed dark so it stays readable.
        </Typography>
      )}
    </Box>
  );
}

ColourField.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string.isRequired,
};

export default function BrandLogoPanel({ axiosInstance, notify }) {
  const { isModuleEnabled } = useModuleEntitlements();
  const docs = DOCS.filter((d) => isModuleEnabled(d.module));
  const [companyId, setCompanyId] = useState(null);
  const [saved, setSaved] = useState({ main: DEFAULT_MAIN, accent: DEFAULT_ACCENT });
  const [main, setMain] = useState(DEFAULT_MAIN);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [saving, setSaving] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewState, setPreviewState] = useState("idle"); // idle | loading | ready | unavailable
  const logo = useCompanyLogo(axiosInstance, companyId);
  const lastUrl = useRef(null);

  useEffect(() => {
    axiosInstance
      .get("/company/info")
      .then(({ data }) => {
        setCompanyId(data?.id || null);
        const colours = savedBrandColors(data);
        setSaved(colours);
        setMain(colours.main);
        setAccent(colours.accent);
      })
      .catch((error) => console.error("Error loading brand settings", error));
  }, [axiosInstance]);

  useEffect(() => {
    if (!doc && docs.length) setDoc(docs[0].key);
  }, [doc, docs]);

  const mainHex = normalizeHex(main, null);
  const accentHex = normalizeHex(accent, null);
  const valid = Boolean(mainHex && accentHex);
  const dirty = valid && (mainHex !== saved.main || accentHex !== saved.accent);

  // Refresh the preview 500 ms after the last change, with the colours as picked.
  const previewKey = useMemo(() => `${doc}|${mainHex}|${accentHex}|${logo.previewUrl || ""}`, [doc, mainHex, accentHex, logo.previewUrl]);
  useEffect(() => {
    if (!doc || !valid) return undefined;
    let cancelled = false;
    setPreviewState("loading");
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`/templates/preview/${doc}`, {
          params: { primary: mainHex, secondary: accentHex },
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
        lastUrl.current = url;
        setPreviewUrl(url);
        setPreviewState("ready");
      } catch (error) {
        if (!cancelled) setPreviewState("unavailable");
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // previewKey carries every input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  useEffect(() => () => lastUrl.current && URL.revokeObjectURL(lastUrl.current), []);

  const save = async () => {
    if (!valid) {
      notify("Colours must be hex values such as #0F2E66.");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put("/company/info", { brand_primary_color: mainHex, brand_secondary_color: accentHex });
      setSaved({ main: mainHex, accent: accentHex });
      setMain(mainHex);
      setAccent(accentHex);
      notify("Brand colours saved. New quotation, order and invoice PDFs use them.");
    } catch (error) {
      notify(`Could not save the colours: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="section" aria-label="Brand and logo" sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px", mb: 2.5, overflow: "hidden" }}>
      <Box sx={{ px: 2.75, pt: 2.25, pb: 1 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: t.text }}>
          Brand & logo
        </Typography>
        <Typography sx={{ fontSize: 14, color: t.text2, mt: 0.25 }}>
          The logo and colours on your quotation, order and invoice PDFs, downloaded or emailed.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1.1fr)" }, gap: 3, px: 2.75, pb: 2.75, pt: 1.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>
              Logo
            </Typography>
            <Typography component="div" sx={{ fontSize: 13, color: t.text2, mb: 1 }}>
              Shown on a white tile in the PDF header. PNG or JPG, up to 5 MB.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{ width: 150, height: 72, borderRadius: "10px", border: `1px solid ${t.border}`, bgcolor: t.surface2, display: "grid", placeItems: "center", overflow: "hidden" }}>
                {logo.previewUrl ? (
                  <Box component="img" src={logo.previewUrl} alt="Company logo" sx={{ maxWidth: "88%", maxHeight: "80%", objectFit: "contain" }} />
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: t.text3 }}>No logo</Typography>
                )}
              </Box>
              <Button variant="outlined" startIcon={<ImageUp size={15} />} onClick={() => setUploadOpen(true)} disabled={!companyId || logo.busy}>
                {logo.previewUrl ? "Replace" : "Upload"}
              </Button>
              {logo.previewUrl && (
                <Button
                  variant="text"
                  color="error"
                  startIcon={<Trash2 size={15} />}
                  disabled={logo.busy}
                  onClick={async () => notify((await logo.remove()) ? "Logo removed." : "Could not remove the logo.")}
                >
                  Remove
                </Button>
              )}
            </Box>
            <AttachmentUploadModal
              isOpen={uploadOpen}
              onClose={() => setUploadOpen(false)}
              onUpload={async (uploadData) => {
                const ok = await logo.upload(uploadData, setUploadOpen);
                notify(ok ? "Logo saved. New PDFs use it." : "Logo uploaded but not saved for PDFs. Try again.");
              }}
              axiosInstance={axiosInstance}
              folderPath="company-logos"
              scope="org"
              allowedFileTypes="image/*"
              maxSizeInMB={5}
            />
          </Box>

          <ColourField label="Main colour" hint="The header band and the total." value={main} onChange={setMain} defaultValue={DEFAULT_MAIN} />
          <ColourField label="Accent colour" hint="The line under the header, the table header and section labels." value={accent} onChange={setAccent} defaultValue={DEFAULT_ACCENT} />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={save} disabled={!dirty || saving}>
              {saving ? "Saving…" : "Save colours"}
            </Button>
            {dirty && (
              <Button
                variant="text"
                onClick={() => {
                  setMain(saved.main);
                  setAccent(saved.accent);
                }}
                disabled={saving}
              >
                Undo changes
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
            <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>
              Preview {dirty ? "(not saved yet)" : ""}
            </Typography>
            {docs.length > 1 && (
              <ToggleButtonGroup size="small" exclusive value={doc} onChange={(_e, v) => v && setDoc(v)} aria-label="Document to preview">
                {docs.map((d) => (
                  <ToggleButton key={d.key} value={d.key} sx={{ px: 1.5, textTransform: "none" }}>
                    {d.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>
          <Box sx={{ position: "relative", height: 560, borderRadius: "10px", border: `1px solid ${t.border}`, bgcolor: t.surface2, overflow: "hidden" }}>
            {previewState === "unavailable" ? (
              <Box sx={{ height: "100%", display: "grid", placeItems: "center", p: 3, textAlign: "center" }}>
                <Typography sx={{ fontSize: 14, color: t.text2 }}>The preview could not be loaded. Your saved colours and logo still apply to PDFs.</Typography>
              </Box>
            ) : !docs.length ? (
              <Box sx={{ height: "100%", display: "grid", placeItems: "center", p: 3 }}>
                <Typography sx={{ fontSize: 14, color: t.text2 }}>Quotes, orders and invoices are not on your plan.</Typography>
              </Box>
            ) : (
              <>
                {previewUrl && <Box component="iframe" title="PDF preview" src={`${previewUrl}#toolbar=0&view=FitH`} sx={{ width: "100%", height: "100%", border: 0 }} />}
                {previewState === "loading" && (
                  <Box sx={{ position: "absolute", top: 10, right: 10 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

BrandLogoPanel.propTypes = {
  axiosInstance: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  notify: PropTypes.func.isRequired,
};
